import { ViewerResolutionSchema, type ViewerResolution } from '@flexcast/domain';

const apiUrl = import.meta.env.VITE_API_URL as string | undefined;

export function viewerId(): string {
  const key = 'flexcast-viewer-id';
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const created = `${crypto.randomUUID()}-${crypto.randomUUID()}`;
  localStorage.setItem(key, created);
  return created;
}

const demoResolution: ViewerResolution = {
  sessionId: '01990a68-dc30-7d52-9d5a-f47d13b6dcaf',
  status: 'live',
  brand: {
    id: '01990a68-dc30-7d52-9d5a-f47d13b6dca1',
    tenantId: '01990a68-dc30-7d52-9d5a-f47d13b6dca2',
    displayName: '105 Miller Street',
    primaryColor: '#116149',
    accentColor: '#C9F27B',
    welcomeText: 'Welcome to the live property presentation.',
    supportText: 'Having trouble? Ask your presenter for help.',
    accessMode: 'public_code',
    questionsEnabled: true,
  },
  playbackUrl: 'https://example.invalid/demo.m3u8',
  playbackToken: 'demo',
  websocketToken: 'demo',
  websocketUrl: 'wss://example.invalid',
  expiresAt: new Date(Date.now() + 60 * 60_000).toISOString(),
  questionsEnabled: true,
};

export async function resolveSession(codeOrSlug: string, pin?: string): Promise<ViewerResolution> {
  if (!apiUrl) {
    await new Promise((resolve) => setTimeout(resolve, 450));
    if (codeOrSlug.replaceAll(' ', '') !== '482731')
      throw new Error('Presentation code is not active.');
    return demoResolution;
  }
  const response = await fetch(`${apiUrl}/v1/viewer/sessions/resolve`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ codeOrSlug: codeOrSlug.replaceAll(' ', ''), pin, viewerId: viewerId() }),
  });
  const data = (await response.json()) as unknown;
  if (!response.ok) {
    const error = data as { message?: string };
    throw new Error(error.message ?? 'Unable to join this presentation.');
  }
  return ViewerResolutionSchema.parse(data);
}

export async function refreshPlayback(
  sessionId: string,
  websocketToken: string,
): Promise<{ playbackToken: string; websocketToken: string; status: string; viewerCount: number }> {
  if (!apiUrl) {
    return { playbackToken: 'demo', websocketToken: 'demo', status: 'live', viewerCount: 0 };
  }
  const response = await fetch(`${apiUrl}/v1/viewer/sessions/${sessionId}/token`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-viewer-authorization': `Bearer ${websocketToken}`,
    },
    body: JSON.stringify({ viewerId: viewerId() }),
  });
  const data = (await response.json()) as {
    playbackToken?: string;
    websocketToken?: string;
    status?: string;
    viewerCount?: number;
    message?: string;
  };
  if (!response.ok || !data.playbackToken || !data.websocketToken) {
    throw new Error(data.message ?? 'Unable to refresh playback authorization.');
  }
  return {
    playbackToken: data.playbackToken,
    websocketToken: data.websocketToken,
    status: data.status ?? 'live',
    viewerCount: data.viewerCount ?? 0,
  };
}
